/**
 * Per-campaign threshold overrides for one condition.
 *
 * Sits under the country overrides and beats them: a campaign listed here is
 * compared against its own value even when its country has an override.
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Plus } from 'lucide-react';

import { Button } from '@/components/ui';
import type { TagRuleCampaignThreshold } from '@/hooks';
import { cn } from '@/lib/utils';

import { CampaignOverrideRow } from './CampaignOverrideRow';
import {
  CampaignThresholdTableHead,
  campaignsAvailableForGroup,
  countAssignedCampaigns,
  flattenUIGroups,
  MAX_CAMPAIGN_THRESHOLD_ROWS,
  othersCampaignCount,
  rowsToUIGroups,
  type CampaignThresholdUIGroup,
} from './campaign-threshold-parts';
import { useCampaignOptions } from './use-campaign-options';

export const CampaignThresholdOverrides: FC<{
  rows: TagRuleCampaignThreshold[];
  onChange: (next: TagRuleCampaignThreshold[]) => void;
  className?: string;
  fields?: ReadonlyArray<{ key: string; label: string }>;
}> = ({ rows, onChange, className, fields }) => {
  const [expanded, setExpanded] = useState(() => rows.length > 0);
  const { options, isLoading } = useCampaignOptions();
  const sorted = useMemo(
    () => [...options].sort((a, b) => a.name.localeCompare(b.name)),
    [options],
  );

  const serialized = JSON.stringify(rows);
  const lastSerialized = useRef(serialized);
  const [groups, setGroups] = useState(() => rowsToUIGroups(rows));

  useEffect(() => {
    if (rows.length === 0) setExpanded(false);
    if (serialized !== lastSerialized.current) {
      lastSerialized.current = serialized;
      setGroups(rowsToUIGroups(rows));
    }
  }, [rows, rows.length, serialized]);

  const assigned = countAssignedCampaigns(rows);
  const atLimit = assigned >= MAX_CAMPAIGN_THRESHOLD_ROWS;

  const commitNext = useCallback(
    (updater: (prev: CampaignThresholdUIGroup[]) => CampaignThresholdUIGroup[]) => {
      setGroups((prev) => {
        const next = updater(prev);
        onChange(flattenUIGroups(next));
        return next;
      });
    },
    [onChange],
  );

  const hasEmptyDraft = groups.some((g) => g.campaigns.length === 0);
  const disableAdd = atLimit || hasEmptyDraft;

  const addRow = useCallback(() => {
    commitNext((prev) => {
      if (countAssignedCampaigns(flattenUIGroups(prev)) >= MAX_CAMPAIGN_THRESHOLD_ROWS) {
        return prev;
      }
      if (prev.some((g) => g.campaigns.length === 0)) return prev;
      return [...prev, { id: `cdraft-${Date.now()}`, campaigns: [], value: 0 }];
    });
  }, [commitNext]);

  const patchGroup = useCallback(
    (
      id: string,
      patch: Partial<Pick<CampaignThresholdUIGroup, 'campaigns' | 'value' | 'compareTo'>>,
    ) => commitNext((prev) => prev.map((g) => (g.id === id ? { ...g, ...patch } : g))),
    [commitNext],
  );

  const removeGroup = useCallback(
    (id: string) => commitNext((prev) => prev.filter((g) => g.id !== id)),
    [commitNext],
  );

  if (!expanded) {
    return (
      <div
        className={cn(
          'flex items-center justify-between gap-2 border-t border-border/60 bg-muted/10 px-3 py-1.5',
          className,
        )}
      >
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ChevronDown className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">
            Campaign overrides
            {assigned > 0 ? ` · ${assigned}` : ''}
          </span>
        </button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          disabled={disableAdd || isLoading}
          onClick={() => {
            setExpanded(true);
            addRow();
          }}
        >
          <Plus className="h-3.5 w-3.5" />
          Add
        </Button>
      </div>
    );
  }

  return (
    <div className={cn('border-t border-border/60 bg-muted/10', className)}>
      <div className="flex items-center justify-between gap-2 border-b border-border/40 px-3 py-1.5">
        <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
          <span className="truncate font-medium text-foreground">Campaign overrides</span>
          {assigned > 0 ? (
            <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary/12 px-1.5 text-[11px] font-semibold tabular-nums text-primary">
              {assigned}
            </span>
          ) : null}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs text-muted-foreground"
          onClick={() => setExpanded(false)}
        >
          <ChevronUp className="h-3.5 w-3.5" />
          Hide
        </Button>
      </div>

      {groups.length > 0 ? <CampaignThresholdTableHead /> : null}
      {groups.map((g) => (
        <CampaignOverrideRow
          key={g.id}
          group={g}
          options={campaignsAvailableForGroup(sorted, groups, g.id)}
          maxKeys={Math.max(
            0,
            MAX_CAMPAIGN_THRESHOLD_ROWS - othersCampaignCount(groups, g.id),
          )}
          isLoading={isLoading}
          onPatch={patchGroup}
          onRemove={removeGroup}
          fields={fields}
        />
      ))}

      <div className="border-t border-border/40 px-3 py-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="h-7 gap-1 text-xs"
          onClick={addRow}
          disabled={disableAdd || isLoading}
        >
          <Plus className="h-3.5 w-3.5" />
          Add row
        </Button>
      </div>
    </div>
  );
};
